import {
  calculateLinearPercentageFromClientX,
  calculatePercentageFromTime,
  calculateTimeFromPercentage,
  clampTimeToTrim,
  useAudioCurrentTimeEventBusSubscriber,
  useAudioDurationEventBusSubscriber,
  useAudioIsPlayingEventBusSubscriber,
  useAudioRequestPlayEventBusPublisher,
  useAudioSetCurrentTimeEventBusPublisher,
} from '@lessonup/pin-renderer';
import { AnchorLink, color, IconTime, SpaceBetween, spacing, styled } from '@lessonup/ui-components';
import { secondsToPrettyTime } from '@lessonup/utils';
import { isNil } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TimeInputError, TimeInputField } from '../../../../../../../../components/TimeInputField/TimeInputField';
import { useEditorTranslation } from '../../../../../../hooks/useEditorTranslation';

interface AudioTrimSettingsProps {
  componentId: string;
  startTime?: number;
  endTime?: number;
  dispatchChange: (settings: { startTime?: number; endTime?: number }) => void;
}

export const AudioTrimSettings: React.FC<AudioTrimSettingsProps> = ({
  componentId,
  startTime = 0,
  endTime = undefined,
  dispatchChange,
}) => {
  const [startError, setStartError] = useState<TimeInputError>(null);
  const [endError, setEndError] = useState<TimeInputError>(null);
  const [currentTime, setCurrentTimeState] = useState<number>(0);
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const [isScrubbing, setIsScrubbing] = useState<boolean>(false);
  const currentMouseMoveHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const currentMouseUpHandlerRef = useRef<(() => void) | null>(null);
  const currentPlayStateRef = useRef(false);
  const { t } = useEditorTranslation();

  const handleSetIsPlaying = useCallback((payload: boolean) => {
    currentPlayStateRef.current = payload;
  }, []);

  useAudioCurrentTimeEventBusSubscriber(componentId, setCurrentTimeState);
  useAudioDurationEventBusSubscriber(componentId, setAudioDuration);
  useAudioIsPlayingEventBusSubscriber(componentId, handleSetIsPlaying);

  const setCurrentTime = useAudioSetCurrentTimeEventBusPublisher(componentId);
  const setPlayState = useAudioRequestPlayEventBusPublisher(componentId);

  const validateTimes = useCallback(
    (newStart?: number, newEnd?: number) => {
      if (newStart !== undefined && newEnd !== undefined && newStart >= newEnd) {
        setStartError({ type: 'negative', message: t('audioPinComponent.startTimeLessThanEndTime') });
        setEndError({ type: 'negative', message: t('audioPinComponent.endTimeGreaterThanStartTime') });
        return false;
      } else {
        setStartError(null);
        setEndError(null);
        return true;
      }
    },
    [t]
  );

  const handleDispatch = useCallback(
    (newStart?: number, newEnd?: number) => {
      if (validateTimes(newStart, newEnd)) {
        if (newStart !== startTime || newEnd !== endTime) {
          dispatchChange({ startTime: newStart, endTime: newEnd });
        }
      }
    },
    [startTime, endTime, dispatchChange, validateTimes]
  );

  const handleStartTimeChange = useCallback(
    (time?: number) => {
      handleDispatch(time, endTime);
    },
    [endTime, handleDispatch]
  );

  const handleEndTimeChange = useCallback(
    (time?: number) => {
      handleDispatch(startTime, time);
    },
    [startTime, handleDispatch]
  );

  const handleStartTimeBlur = useCallback(
    (time?: number) => {
      if (!isNil(time)) {
        handleDispatch(time, endTime);
      }
    },
    [endTime, handleDispatch]
  );

  const handleEndTimeBlur = useCallback(
    (time?: number) => {
      if (!isNil(time)) {
        handleDispatch(startTime, time);
      }
    },
    [startTime, handleDispatch]
  );

  const handleStartTimeReset = useCallback(() => {
    handleDispatch(0, endTime);
  }, [endTime, handleDispatch]);

  const handleEndTimeReset = useCallback(() => {
    handleDispatch(startTime, undefined);
  }, [startTime, handleDispatch]);

  const handleSetStartTime = useCallback(() => {
    const time = currentTime;
    handleDispatch(time, endTime);
  }, [currentTime, endTime, handleDispatch]);

  const handleSetEndTime = useCallback(() => {
    const time = currentTime;
    handleDispatch(startTime, time);
  }, [currentTime, startTime, handleDispatch]);

  const getProgressPercentage = useCallback(() => {
    return calculatePercentageFromTime({
      currentTime,
      duration: audioDuration,
    });
  }, [currentTime, audioDuration]);

  const getStartMarkerPosition = useCallback(() => {
    return calculatePercentageFromTime({
      currentTime: startTime,
      duration: audioDuration,
    });
  }, [startTime, audioDuration]);

  const getEndMarkerPosition = useCallback(() => {
    return calculatePercentageFromTime({
      currentTime: endTime ?? audioDuration,
      duration: audioDuration,
    });
  }, [endTime, audioDuration]);

  // Scrubbing logic
  const calculateTimeFromPosition = useCallback(
    (clientX: number, rect: DOMRect) => {
      const percentage = calculateLinearPercentageFromClientX({
        clientX,
        rect,
      });

      return calculateTimeFromPercentage({
        percentage,
        duration: audioDuration,
      });
    },
    [audioDuration]
  );

  const seekToTime = useCallback(
    (time: number) => {
      const newTime = clampTimeToTrim({
        time,
        duration: audioDuration,
      });
      setCurrentTime(newTime);
    },
    [audioDuration, setCurrentTime]
  );

  const handleProgressMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const playingOnStart = currentPlayStateRef.current;
      // // Remember if audio was playing before we started scrubbing

      // // Pause playback during scrubbing
      if (playingOnStart) {
        setPlayState(false);
      }

      setIsScrubbing(true);

      // Update position immediately on mouse down
      const rect = event.currentTarget.getBoundingClientRect();
      const time = calculateTimeFromPosition(event.clientX, rect);
      seekToTime(time);

      const handleMouseMove = (e: MouseEvent) => {
        const time = calculateTimeFromPosition(e.clientX, rect);
        seekToTime(time);
      };

      const handleMouseUp = () => {
        setIsScrubbing(false);

        if (playingOnStart) {
          setPlayState(true);
        }

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        currentMouseMoveHandlerRef.current = null;
        currentMouseUpHandlerRef.current = null;
      };

      currentMouseMoveHandlerRef.current = handleMouseMove;
      currentMouseUpHandlerRef.current = handleMouseUp;

      // Add global event listeners
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [calculateTimeFromPosition, seekToTime, setPlayState]
  );

  const handleProgressClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const time = calculateTimeFromPosition(event.clientX, rect);
      seekToTime(time);
    },
    [calculateTimeFromPosition, seekToTime]
  );

  // Cleanup effect to remove event listeners on unmount while scrubbing
  useEffect(() => {
    return () => {
      // Clean up any active document event listeners on unmount
      if (currentMouseMoveHandlerRef.current) {
        document.removeEventListener('mousemove', currentMouseMoveHandlerRef.current);
        currentMouseMoveHandlerRef.current = null;
      }
      if (currentMouseUpHandlerRef.current) {
        document.removeEventListener('mouseup', currentMouseUpHandlerRef.current);
        currentMouseUpHandlerRef.current = null;
      }
    };
  }, []);

  return (
    <SpaceBetween direction="y" spacing={spacing.size16}>
      {/* Progress Bar */}
      <SpaceBetween direction="y" spacing={spacing.size8}>
        <TimeDisplayContainer>
          <TimeLabel>{secondsToPrettyTime(currentTime)}</TimeLabel>
          <TimeLabel>{secondsToPrettyTime(audioDuration)}</TimeLabel>
        </TimeDisplayContainer>
        <ProgressBarContainer
          role="slider"
          tabIndex={0}
          aria-label={t('audioPinComponent.audioProgressBar')}
          aria-valuemin={0}
          aria-valuemax={audioDuration}
          aria-valuenow={currentTime}
          isScrubbing={isScrubbing}
          onMouseDown={handleProgressMouseDown}
          onClick={handleProgressClick}
        >
          <ProgressFill isScrubbing={isScrubbing} style={{ width: `${getProgressPercentage()}%` }} />
          {startTime > 0 && <Marker style={{ left: `${getStartMarkerPosition()}%` }} />}
          {endTime !== undefined && <Marker style={{ left: `${getEndMarkerPosition()}%` }} />}
        </ProgressBarContainer>
      </SpaceBetween>
      <SpaceBetween direction="y" spacing={spacing.size4}>
        <TimeInputField
          label={t('audioPinComponent.startTimeInSeconds')}
          id="input-audio-starttime"
          initialValue={startTime}
          onValidChange={handleStartTimeChange}
          externalError={startError}
          onBlur={handleStartTimeBlur}
          onReset={handleStartTimeReset}
        />
        <AnchorLink onClick={handleSetStartTime} iconStart={<IconTime />}>
          {t('audioPinComponent.setCurrentTime')}
        </AnchorLink>
      </SpaceBetween>
      <SpaceBetween direction="y" spacing={spacing.size4}>
        <TimeInputField
          label={t('audioPinComponent.endTimeInSeconds')}
          id="input-audio-endtime"
          initialValue={endTime}
          onValidChange={handleEndTimeChange}
          externalError={endError}
          onBlur={handleEndTimeBlur}
          onReset={handleEndTimeReset}
        />
        <AnchorLink onClick={handleSetEndTime} iconStart={<IconTime />}>
          {t('audioPinComponent.setCurrentTime')}
        </AnchorLink>
      </SpaceBetween>
    </SpaceBetween>
  );
};

const TimeDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 90%;
`;

const TimeLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

interface ProgressBarContainerProps {
  isScrubbing: boolean;
}

const height = 8;
const ProgressBarContainer = styled.div<ProgressBarContainerProps>`
  position: relative;
  width: 90%;
  height: ${height}px;
  background-color: ${color.neutral.surface3.background};
  border-radius: 4px;
  cursor: ${({ isScrubbing }: ProgressBarContainerProps) => (isScrubbing ? 'grabbing' : 'pointer')};
`;

interface ProgressFillProps {
  isScrubbing: boolean;
}

const ProgressFill = styled.div<ProgressFillProps>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background-color: ${color.hierarchy.secondary.fill};
  border-radius: ${spacing.size4};
  transition: ${({ isScrubbing }: ProgressFillProps) => (isScrubbing ? 'none' : 'width 0.1s ease')};
`;

const Marker = styled.div`
  position: absolute;
  top: -1px;
  width: ${spacing.size4};
  height: ${height + 2}px;
  background-color: ${color.utils.focus.stroke};
  border-radius: 1px;
  transform: translateX(-50%);
`;
